import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  LogIn,
  Mail,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  X,
  RefreshCw,
  ShieldAlert
} from "lucide-react";
import AuthContext from "../context/AuthContext";
import api from "../config/api";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    captcha: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaData, setCaptchaData] = useState(null);
  const [loadingCaptcha, setLoadingCaptcha] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  // Fetch CAPTCHA on component mount
  useEffect(() => {
    fetchCaptcha();
  }, []);

  // Show success message if redirected from registration
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear message after 5 seconds
      setTimeout(() => setSuccessMessage(""), 5000);
    }
  }, [location.state]);

  // Fetch CAPTCHA from backend (SVG)
  const fetchCaptcha = async () => {
    setLoadingCaptcha(true);
    try {
      const res = await api.get("/captcha/generate");
      if (res.data.success) {
        setCaptchaData(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch CAPTCHA:", err);
      setError("Failed to load CAPTCHA. Please refresh the page.");
    } finally {
      setLoadingCaptcha(false);
    }
  };

  const getRedirectPath = (role) => {
    switch (role) {
      case "admin":
        return "/admin-dashboard";
      case "co-admin":
        return "/coadmin";
      case "accountant":
        return "/accountant";
      default:
        return "/dashboard";
    }
  };

  const { email, password, captcha } = formData;

  // Button should be disabled if captcha is empty or is submitting
  const isButtonDisabled = !captcha || isSubmitting || !captchaData;

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate CAPTCHA
    if (!captcha || !captchaData) {
      setError("Please enter the CAPTCHA code");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await login(
        email.toLowerCase(),
        password,
        captchaData.captchaId,
        captcha,
      );
      if (result && !result.error) {
        const redirectPath = from !== "/" ? from : getRedirectPath(result.role);
        navigate(redirectPath, { replace: true });
      } else {
        const errorMessage = result?.error || "Login failed";
        if (errorMessage.includes("CAPTCHA")) {
          setError(errorMessage);
          fetchCaptcha();
          setFormData({ ...formData, captcha: "" });
        } else if (errorMessage === "Invalid credentials") {
          setError(
            "Invalid email or password. Please check your credentials and try again."
          );
          fetchCaptcha();
          setFormData({ ...formData, captcha: "" });
        } else if (
          errorMessage ===
          "Your account is pending deletion and cannot be accessed. Please contact support if this is a mistake."
        ) {
          setError(
            "Your account is pending deletion. Please contact support for assistance."
          );
        } else {
          setShowModal(true);
          setCountdown(5);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Countdown timer for auto-redirect
  useEffect(() => {
    let timer;
    if (showModal && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (showModal && countdown === 0) {
      navigate("/register");
    }
    return () => clearTimeout(timer);
  }, [showModal, countdown, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 font-sans px-4 py-16 relative overflow-hidden">
      {/* Background soft meshes */}
      <div className="absolute top-0 inset-x-0 h-full w-full bg-gradient-to-br from-indigo-50/50 via-slate-50 to-blue-50/30 -z-10"></div>
      
      <div className="bg-white rounded-3xl border border-slate-200 p-8 sm:p-10 max-w-md w-full shadow-sm relative">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-indigo-100/60 shadow-sm">
            <LogIn className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 mb-2 tracking-tight">
            Welcome Back
          </h1>
          <p className="text-slate-500 text-sm">
            Sign in to your account or{" "}
            <Link
              to="/register"
              className="font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              create a new account
            </Link>
          </p>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
            <p className="text-emerald-800 text-xs sm:text-sm font-semibold">
              {successMessage}
            </p>
          </div>
        )}

        <form className="space-y-5" onSubmit={onSubmit}>
          <div className="space-y-1.5">
            <label
              htmlFor="email"
              className="text-sm font-bold text-slate-700 ml-1"
            >
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={onChange}
                className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm text-slate-800 transition-all"
                placeholder="Enter your email"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="password"
              className="text-sm font-bold text-slate-700 ml-1"
            >
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={onChange}
                className="w-full pl-12 pr-12 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm text-slate-800 transition-all"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-indigo-600 transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* CAPTCHA Section */}
          <div className="space-y-1.5">
            <label
              htmlFor="captcha"
              className="text-sm font-bold text-slate-700 ml-1"
            >
              Security CAPTCHA
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-2xl p-2.5 justify-center min-h-[52px]">
                {loadingCaptcha ? (
                  <RefreshCw className="w-5 h-5 animate-spin text-indigo-600" />
                ) : captchaData && captchaData.svg ? (
                  <span
                    className="captcha-svg flex items-center justify-center"
                    dangerouslySetInnerHTML={{ __html: captchaData.svg }}
                    style={{ display: "inline-block", width: 120, height: 40 }}
                  />
                ) : (
                  <span className="text-slate-400 text-xs font-semibold">----</span>
                )}
              </div>
              <button
                type="button"
                onClick={fetchCaptcha}
                disabled={loadingCaptcha}
                className="p-3.5 border border-slate-200 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-indigo-600 transition-all disabled:opacity-50"
                title="Refresh CAPTCHA"
              >
                <RefreshCw
                  className={`w-5 h-5 ${loadingCaptcha ? "animate-spin" : ""}`}
                />
              </button>
            </div>
            
            <input
              id="captcha"
              name="captcha"
              type="text"
              required
              value={captcha}
              onChange={onChange}
              maxLength={4}
              className="mt-2 w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm text-slate-800 transition-all uppercase placeholder-slate-400 font-bold tracking-widest text-center"
              placeholder="ENTER CODE"
              style={{ textTransform: "uppercase" }}
            />
            <p className="text-[10px] text-slate-400 font-medium ml-1">
              Enter the 4-character code shown above.
            </p>
          </div>

          <div className="flex items-center justify-end">
            <Link
              to="/forgot-password"
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              Forgot Password?
            </Link>
          </div>

          {error && (
            <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm text-rose-700 font-semibold leading-relaxed">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isButtonDisabled}
            className={`w-full flex justify-center py-4 px-4 border border-transparent rounded-2xl text-base font-bold text-white shadow-md shadow-indigo-100/50 hover-glow transition-all duration-200 active:scale-[0.98] ${
              isButtonDisabled ? "bg-slate-200 text-slate-400 cursor-not-allowed shadow-none" : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            {isSubmitting ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>

      {/* Modal for unregistered user */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm z-50 p-4">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-6 sm:p-8 max-w-sm w-full text-center relative animate-[fadeInUp_0.3s_ease-out]">
            <div className="flex justify-between items-center mb-6">
              <ShieldAlert className="w-10 h-10 text-rose-500 mx-auto" />
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <h2 className="text-xl font-extrabold text-slate-900 mb-2">
              Account Not Found
            </h2>
            <p className="text-slate-500 text-sm mb-4 leading-relaxed">
              We couldn't find a record for this email. Would you like to create a new account?
            </p>
            <p className="text-xs text-indigo-600 font-bold mb-6 uppercase tracking-wider">
              Redirecting to sign-up in {countdown}s...
            </p>
            <div className="flex gap-3 justify-center">
              <Link
                to="/register"
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors text-sm shadow-sm"
                onClick={() => setShowModal(false)}
              >
                Register Now
              </Link>
              <button
                onClick={() => setShowModal(false)}
                className="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200 transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
